import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { identifyLead, trackEvent } from "../lib/analytics";
import { getMakdisOsEndpoint } from "../lib/makdisOsClient";

export default function LeadMagnetForm({
  copy,
  niche,
  asset,
  downloadUrl,
  endpoint = getMakdisOsEndpoint(),
  onSuccess,
}) {
  const labels = copy?.fields || {};
  const hints = copy?.hints || {};
  const buttons = copy?.buttons || {};
  const messages = copy?.messages || {};

  const [values, setValues] = useState({
    name: "",
    email: "",
    company: "",
  });
  const [touched, setTouched] = useState({});
  const [utm, setUtm] = useState({
    source: "",
    medium: "",
    campaign: "",
    content: "",
  });
  const [status, setStatus] = useState("idle");
  const formStartedRef = useRef(false);

  const validEmail = /.+@.+\..+/.test(values.email);
  const canSubmit = values.name && validEmail && status !== "submitting";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setUtm({
      source: params.get("utm_source") || "",
      medium: params.get("utm_medium") || "",
      campaign: params.get("utm_campaign") || "",
      content: params.get("utm_content") || "",
    });
  }, []);

  const payload = useMemo(
    () => ({
      type: "lead_magnet",
      conversionPoint: "lead_magnet",
      crmLifecycleStage: "Lead",
      niche,
      assetKey: asset?.key || niche || "",
      assetTitle: asset?.title || "",
      route: asset?.route || "",
      downloadUrl: downloadUrl || "",
      name: values.name,
      email: values.email,
      company: values.company,
      utm_source: utm.source,
      utm_medium: utm.medium,
      utm_campaign: utm.campaign,
      utm_content: utm.content,
      locale: document.documentElement.lang || "nl",
      page: window.location.pathname,
      sourcePath: window.location.pathname,
      timestamp: new Date().toISOString(),
    }),
    [
      asset?.key,
      asset?.route,
      asset?.title,
      downloadUrl,
      niche,
      utm.campaign,
      utm.content,
      utm.medium,
      utm.source,
      values,
    ]
  );

  const markFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;

    trackEvent("lead_magnet_form_start", {
      path: window.location.pathname,
      asset_key: asset?.key || niche || "",
      niche,
    });
  };

  const set = (key) => (event) => {
    markFormStarted();
    setValues((current) => ({ ...current, [key]: event.target.value }));
  };

  const mark = (key) => () =>
    setTouched((current) => ({ ...current, [key]: true }));

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setStatus("submitting");
    trackEvent("cta_click", {
      path: window.location.pathname,
      label: buttons.submit || "Lead magnet submit",
      destination: endpoint || "",
      source: "lead-magnet-form",
      asset_key: asset?.key || niche || "",
      niche,
      route: asset?.route || "",
    });

    try {
      if (!endpoint) {
        throw new Error("Lead capture endpoint is not configured");
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Lead capture failed with status ${response.status}`);
      }

      trackEvent("lead_magnet_submit", {
        path: window.location.pathname,
        asset_key: asset?.key || niche || "",
        niche,
        route: asset?.route || "",
      });
      identifyLead(values.email, {
        lead_type: "lead_magnet",
        asset_key: asset?.key || niche || "",
        niche,
        route: asset?.route || "",
        source_path: window.location.pathname,
      });
      setStatus("success");
      onSuccess?.(payload);
    } catch (error) {
      console.error(error);
      setStatus("error");
    }
  };

  return (
    <Card
      as={motion.div}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ duration: 0.5, ease: [0.22, 0.61, 0.36, 1] }}
    >
      <Form onSubmit={submit}>
        <Field>
          <Label>{labels.name}</Label>
          <Input
            aria-label={labels.name}
            value={values.name}
            onChange={set("name")}
            onBlur={mark("name")}
            aria-invalid={Boolean(touched.name && !values.name)}
            required
          />
          <Hint $show={Boolean(touched.name && !values.name)}>
            {hints.required}
          </Hint>
        </Field>
        <Field>
          <Label>{labels.email}</Label>
          <Input
            type="email"
            aria-label={labels.email}
            value={values.email}
            onChange={set("email")}
            onBlur={mark("email")}
            aria-invalid={Boolean(touched.email && !validEmail)}
            required
          />
          <Hint $show={Boolean(touched.email && !validEmail)}>
            {hints.email}
          </Hint>
        </Field>
        <Field>
          <Label>{labels.company}</Label>
          <Input
            aria-label={labels.company}
            value={values.company}
            onChange={set("company")}
          />
        </Field>

        <HiddenInput type="hidden" name="utm_source" value={utm.source} readOnly />
        <HiddenInput type="hidden" name="utm_medium" value={utm.medium} readOnly />
        <HiddenInput
          type="hidden"
          name="utm_campaign"
          value={utm.campaign}
          readOnly
        />
        <HiddenInput type="hidden" name="utm_content" value={utm.content} readOnly />

        <SubmitButton type="submit" disabled={!canSubmit}>
          {status === "submitting" ? buttons.submitting : buttons.submit}
        </SubmitButton>

        {status === "success" ? (
          <StatusMessage>{messages.success}</StatusMessage>
        ) : null}
        {status === "error" ? (
          <StatusMessage $error>{messages.error}</StatusMessage>
        ) : null}
      </Form>
    </Card>
  );
}

const Card = styled.div`
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background:
    linear-gradient(
      145deg,
      rgba(255, 255, 255, 0.08),
      rgba(255, 255, 255, 0.03)
    ),
    rgba(8, 8, 8, 0.56);
  backdrop-filter: blur(14px);
  padding: 28px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
`;

const Form = styled.form`
  display: grid;
  gap: 18px;
`;

const Field = styled.label`
  display: grid;
  gap: 8px;
`;

const Label = styled.span`
  font-size: 12px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  font-size: 16px;
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:focus {
    border-color: rgba(255, 255, 255, 0.34);
    background: rgba(255, 255, 255, 0.06);
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const Hint = styled.span`
  min-height: 18px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.65);
  opacity: ${(p) => (p.$show ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

const SubmitButton = styled.button`
  margin-top: 6px;
  min-height: 52px;
  border: 0;
  border-radius: 18px;
  background: #fff;
  color: #000;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    transform: translateY(-1px);
  }
`;

const StatusMessage = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: ${(p) =>
    p.$error ? "rgba(255, 182, 182, 0.92)" : "rgba(255, 255, 255, 0.78)"};
`;
